import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { analysisPageRoute, CANONICAL_ANALYSIS_SEGMENT } from '../routing';

/**
 * Which app serves the report while the rollout flag decides.
 *
 * Four URLs answer for it: the taxonomy rename doubles the segment
 * (`/analysis` and `/reports`), and the page being replaced lives a segment
 * deeper (`/journeys/...`). Hydra claims all four so that an in-app link to
 * any of them reaches this app, and then these two send the reader to
 * whichever app is serving the report.
 *
 * Both directions matter. With the flag on, a reader who followed an old link
 * is forwarded to the canonical URL; with it off, a reader who reached a
 * Hydra-shaped URL is handed back, which is what makes the flag a real
 * kill-switch rather than a way to reach a broken page.
 */

/** The `journeys` twin's URL, with the query and hash carried; `null` while the workspace resolves. */
function useLegacyReportHref(): string | null {
  const router = useRouter();

  return useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/journeys/reports', params: { workspaceId } }).href,
  );
}

/** The canonical Hydra URL, with the query and hash carried; `null` while the workspace resolves. */
function useMigratedReportHref(): string | null {
  const router = useRouter();

  return useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({
        to: analysisPageRoute(CANONICAL_ANALYSIS_SEGMENT, 'report'),
        params: { workspaceId },
      }).href,
  );
}

export type ReportRouteProps = {
  children: ReactNode;
};

/**
 * A Hydra-shaped URL. Serves the report where the flag is on, and hands the
 * reader back to the page being replaced where it is off.
 */
export function ReportMigrationRoute({ children }: ReportRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.AnalysisMigration);
  const legacyHref = useLegacyReportHref();
  // Readiness gates on the resolved workspace rather than the URL segment.
  // The sentinel resolver above lets the frame through while the workspaces
  // query is still pending — it has no id to redirect to yet — and the
  // report's reads go through `requireWorkspaceId`, which throws rather than
  // waiting. The account, and so the flag, can land first.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      {children}
    </MigrationRouteView>
  );
}

/**
 * The URL the page being replaced answers on. Renders that app where the flag
 * is off, and forwards to the canonical Hydra URL where it is on.
 */
export function LegacyReportHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.AnalysisMigration);
  const migratedHref = useMigratedReportHref();

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}
